/**
 * royaleSocketRouting.ts
 *
 * Translates inbound fun_* envelopes into funChallengesRoyaleStore actions.
 * Kept SEPARATE from useFunChallengesSocket's 1v1 handlers so the royale
 * branches are additive — each is invoked only when
 * `gameType === 'fun_puzzle_royale'`, leaving the shipped ST/PTB/PDB paths
 * byte-identical.
 *
 * The server reuses the same room/session lifecycle as the 1v1 modes:
 *   fun_room_created / fun_room_update → lobby roster
 *   fun_session_start                 → start the race
 *   fun_move_broadcast (+combo/points)→ live score update (self + others)
 *   fun_player_eliminated             → mark a player out (non-terminating)
 *   fun_session_end                   → final standings
 *   fun_session_rejoin                → bulk hydrate after refresh
 */

import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import {BattleType} from '@/Screens/FunChallenges/types';
import type {
  RoyaleEndPayload,
  RoyaleLobbyPlayer,
  RoyaleSession,
  RoyaleSettings,
} from '@/Screens/FunChallenges/royaleTypes';

function nowMs(): number {
  return Date.now();
}

/**
 * One-shot "next room created" resolver. The classroom-create flow needs the
 * REAL room_id of the lobby IT just created, but it cannot reliably read it off
 * the subscribed store snapshot: on a 2nd+ create the store can still hold the
 * PREVIOUS room (a fun_room_created reply that landed on the host's separate
 * battle tab under cc-conn-mgr's single-sid-per-user routing never refreshed
 * the classroom tab), so the register effect re-POSTed the stale id → Django
 * saw a duplicate room_id → no new row → "create does nothing". Instead the
 * classroom create arms this resolver right before sending fun_room_create;
 * routeRoyaleRoomCreated fires it with the authoritative payload the instant
 * the ACK arrives, so the classroom tab registers the EXACT new id regardless
 * of store timing. Single-shot: cleared after firing.
 */
type RoomCreatedResolver = (info: {
  roomId: string;
  shareCode: string;
  expiresAt: number;
}) => void;
let pendingRoomCreatedResolver: RoomCreatedResolver | null = null;

export function armRoyaleRoomCreatedResolver(fn: RoomCreatedResolver): void {
  pendingRoomCreatedResolver = fn;
}
export function clearRoyaleRoomCreatedResolver(): void {
  pendingRoomCreatedResolver = null;
}

/**
 * True when the current tab is the CLASSROOM launcher page. The coach plays a
 * royale in a SEPARATE battle tab; the classroom tab is only a launcher (Create
 * / Open buttons). But cc-conn-mgr routes broadcasts by userId to ONE socket,
 * so a royale's fun_room_update / fun_session_start / fun_session_rejoin meant
 * for the coach's battle tab can land on the classroom tab instead. Without a
 * guard those drive the classroom tab's royale store into 'lobby'/'in_battle'
 * — which (a) shows the "challenge in progress" pill there and (b) leaves the
 * store dirty so the NEXT create can't get a clean idle slate, breaking the
 * coach's 2nd+ create. We mirror the same /classroom guard the global listener
 * already uses on its navigate effects: on the classroom route we DON'T let a
 * participant session drive the store. (fun_room_created is intentionally NOT
 * guarded — the classroom create resolver depends on it firing here.)
 */
function onClassroomRoute(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.location?.pathname === 'string' &&
    window.location.pathname.toLowerCase().includes('/classroom')
  );
}

interface RoomPlayerWire {
  userId: string;
  username?: string;
  avatarUrl?: string;
}

/** Build roster entries; flag the host (settings.hostId) when known. */
function toLobbyPlayers(
  players: RoomPlayerWire[] | undefined,
  hostId: string | undefined,
): RoyaleLobbyPlayer[] {
  return (players || []).map(p => ({
    userId: String(p.userId),
    name: p.username || '',
    avatarUrl: p.avatarUrl,
    isHost: !!hostId && String(p.userId) === String(hostId),
  }));
}

/**
 * fun_room_created (royale): confirm the server-assigned roomId on the lobby
 * the façade already seeded optimistically at send time. If that optimistic
 * lobby is missing (edge case), seed a minimal one so the waiting room renders.
 * roomId doubles as the join/share code.
 */
export function routeRoyaleRoomCreated(
  payload: {
    roomId: string;
    creatorId: string;
    shareCode?: string;
    expiresAt?: number;
  },
  _gameType: string,
  myUserId: string,
): void {
  const store = useFunChallengesRoyaleStore.getState();
  const hostId = String(payload.creatorId);
  // Prefer the server's short share code; fall back to the roomId only if a
  // pre-share-code server build is running.
  const shareCode = payload.shareCode || payload.roomId;
  // Server-authoritative lobby expiry (#1) — overrides the optimistic seed so
  // the host's countdown matches what joiners will get.
  const expiresAt =
    Number(payload.expiresAt) || store.lobby?.expiresAt || nowMs() + 15 * 60 * 1000;
  // [royale-dbg] confirms the ACK reached THIS tab + whether a resolver is armed
  console.log('[royale-dbg] routeRoyaleRoomCreated ON THIS TAB', {
    roomId: payload.roomId,
    onClassroomRoute: onClassroomRoute(),
    resolverArmed: !!pendingRoomCreatedResolver,
    path: window?.location?.pathname,
  });
  // Hand the authoritative new room to a one-shot resolver if the classroom
  // create flow armed one — BEFORE touching the store, so it fires even on the
  // fallback path and never depends on store timing.
  if (pendingRoomCreatedResolver) {
    const resolve = pendingRoomCreatedResolver;
    pendingRoomCreatedResolver = null;
    try {
      resolve({roomId: payload.roomId, shareCode, expiresAt});
    } catch {
      // non-fatal — the store update below still runs
    }
  }
  if (store.lobby) {
    // Confirm/refresh the roomId + shareCode on the existing optimistic lobby.
    store.setLobby(
      {
        ...store.lobby,
        roomId: payload.roomId,
        shareCode,
        hostId,
        expiresAt,
      },
      String(myUserId || store.myUserId || hostId),
    );
    return;
  }
  // Fallback: minimal lobby with default-ish settings (host as sole member).
  store.setLobby(
    {
      roomId: payload.roomId,
      shareCode,
      hostId,
      hostIsPlayer: true,
      state: 'waiting',
      players: [{userId: hostId, name: '', avatarUrl: undefined, isHost: true}],
      spectators: [],
      settings: {} as RoyaleSettings,
      expiresAt,
    },
    String(myUserId),
  );
}

interface SpectatorWire {
  userId: string;
  username?: string;
  avatarUrl?: string;
}

function toSpectators(
  list: SpectatorWire[] | undefined,
): Array<{userId: string; name: string; avatarUrl?: string}> {
  return (list || []).map(s => ({
    userId: String(s.userId),
    name: s.username || '',
    avatarUrl: s.avatarUrl,
  }));
}

/** fun_room_update (royale): refresh the roster + settings/host, OR a
 *  spectator-roster-only broadcast, OR a pre-start spectator's snapshot. */
export function routeRoyaleRoomUpdate(
  payload: {
    players?: RoomPlayerWire[];
    state?: string;
    roomId?: string;
    settings?: RoyaleSettings;
    hostId?: string;
    spectators?: SpectatorWire[];
    /** Server marks the snapshot sent to a JOINING pre-start spectator. */
    forSpectator?: boolean;
  },
  _gameType?: string,
  myUserId?: string,
): void {
  // A royale roster broadcast routed to the classroom launcher tab (single-sid)
  // would setLobby() → status 'lobby' and re-infect it. The coach's lobby/race
  // lives in the battle tab; the classroom tab only creates+registers then
  // resets. Ignore roster updates here. See onClassroomRoute.
  if (onClassroomRoute()) {
    return;
  }
  const store = useFunChallengesRoyaleStore.getState();
  // Server-authoritative lobby expiry (same epoch for everyone) — prefer it so
  // joiners' countdowns are in sync with the host's (#1). Fall back to an
  // existing lobby's value, then a local default.
  const resolveExpiresAt = (): number =>
    Number((payload.settings as any)?.expiresAt) ||
    store.lobby?.expiresAt ||
    nowMs() + 15 * 60 * 1000;
  const wireHostId = payload.hostId ? String(payload.hostId) : '';

  // Always refresh the spectator roster when the payload carries it.
  if (payload.spectators) {
    store.setSpectators(toSpectators(payload.spectators));
  }

  // Spectator-roster-ONLY broadcast (no players/settings) — don't touch the
  // player roster/settings; we've already applied the spectators above.
  if (!payload.players && !payload.forSpectator) {
    return;
  }

  // A pre-start spectator's snapshot: seed a WAITING lobby flagged spectator so
  // they sit in the waiting room read-only and auto-transition on start (#2/#3).
  if (payload.forSpectator) {
    const me = String(myUserId || store.myUserId || '');
    store.setLobbyAsSpectator(
      {
        roomId: payload.roomId || '',
        shareCode: payload.roomId || '',
        hostId: wireHostId,
        hostIsPlayer: true,
        state: 'waiting',
        players: toLobbyPlayers(payload.players, wireHostId || undefined),
        spectators: (payload.spectators || []).map(s => String(s.userId)),
        settings: (payload.settings || {}) as RoyaleSettings,
        expiresAt: resolveExpiresAt(),
      },
      me,
    );
    if (payload.spectators) {
      store.setSpectators(toSpectators(payload.spectators));
    }
    return;
  }

  // Defensive seed: a joiner who somehow reached here without an optimistic
  // lobby (or whose lobby was reset) still needs a waiting room.
  if (!store.lobby) {
    const me = String(myUserId || store.myUserId || '');
    store.setLobby(
      {
        roomId: payload.roomId || '',
        shareCode: payload.roomId || '',
        hostId: wireHostId,
        hostIsPlayer: true,
        state: 'waiting',
        players: toLobbyPlayers(payload.players, wireHostId || undefined),
        spectators: [],
        settings: (payload.settings || {}) as RoyaleSettings,
        expiresAt: resolveExpiresAt(),
      },
      me,
    );
    return;
  }
  // Patch the existing lobby: roster + (newly-known) settings/host.
  const hostId = wireHostId || store.lobby.hostId;
  const settings =
    payload.settings && Object.keys(payload.settings).length > 0
      ? payload.settings
      : store.lobby.settings;
  // Sync the TOP-LEVEL hostIsPlayer from settings (the host's play/spectate
  // toggle rides settings.hostIsPlayer). Without this the lobby's top-level
  // flag stayed stale and other clients never dropped the spectate-host from
  // their player list (#4).
  const hostIsPlayer =
    typeof (settings as any)?.hostIsPlayer === 'boolean'
      ? (settings as any).hostIsPlayer
      : store.lobby.hostIsPlayer;
  store.setLobby(
    {
      ...store.lobby,
      // CRITICAL: backfill the REAL roomId from the server (the optimistic
      // join seed set roomId = the share CODE). FUN_ROOM_START keys on the
      // real roomId — if we leave the code here, the host's "Start" sends the
      // wrong id and the race never starts (#4). Keep the existing shareCode.
      roomId: payload.roomId || store.lobby.roomId,
      hostId,
      hostIsPlayer,
      settings,
      players: toLobbyPlayers(payload.players, hostId),
      state: payload.state === 'in_session' ? 'in_session' : 'waiting',
      expiresAt: resolveExpiresAt(),
    },
    String(myUserId || store.myUserId || ''),
  );
  // setLobby resets spectators-derived state? No — it spreads initialState but
  // we re-apply the roster here so the Spectators tab stays populated.
  if (payload.spectators) {
    store.setSpectators(toSpectators(payload.spectators));
  }
}

/** fun_session_start (royale): begin the race. */
export function routeRoyaleSessionStart(
  payload: {
    sessionId: string;
    roomId: string;
    playerIds: string[];
    players?: RoomPlayerWire[];
    settings: RoyaleSettings;
    shareCode?: string;
    startsAt?: string;
    endsAt: string;
    coords: string[];
    watcherId?: string;
  },
  myUserId: string,
): void {
  // Don't let a participant session start drive the classroom launcher tab
  // into 'in_battle' — the coach plays in a separate battle tab. See
  // onClassroomRoute.
  if (onClassroomRoute()) {
    return;
  }
  // Ensure the shareCode is on settings (the Spectators tab reads it post-start
  // from session.settings.shareCode — #6).
  const startSettings: RoyaleSettings = {
    ...payload.settings,
    shareCode:
      (payload.settings as any)?.shareCode || payload.shareCode || '',
  };
  const session: RoyaleSession = {
    sessionId: payload.sessionId,
    roomId: payload.roomId,
    myUserId: String(myUserId),
    battleType: BattleType.PUZZLE_ROYALE,
    startsAt: parseInt(payload.startsAt || '', 10) || 0,
    endsAt: parseInt(payload.endsAt, 10) || 0,
    settings: startSettings,
    coords: payload.coords || [],
    watcherId: String(payload.watcherId || ''),
  };
  // Prefer the server-sent roster (authoritative usernames); fall back to the
  // lobby snapshot for any id it doesn't carry.
  const store = useFunChallengesRoyaleStore.getState();
  const hostId = String(payload.settings?.hostId || store.lobby?.hostId || '');
  const wireById: Record<string, RoomPlayerWire> = {};
  (payload.players || []).forEach(p => {
    wireById[String(p.userId)] = p;
  });
  const lobbyById: Record<string, RoyaleLobbyPlayer> = {};
  (store.lobby?.players || []).forEach(p => {
    lobbyById[String(p.userId)] = p;
  });
  const players: RoyaleLobbyPlayer[] = (payload.playerIds || []).map(id => {
    const key = String(id);
    const wire = wireById[key];
    const known = lobbyById[key];
    return {
      userId: key,
      name: wire?.username || known?.name || '',
      avatarUrl: wire?.avatarUrl || known?.avatarUrl,
      isHost: known?.isHost || (!!hostId && key === hostId),
    };
  });
  store.startSession(session, players, nowMs());
  if ((payload as any).spectators) {
    store.setSpectators(toSpectators((payload as any).spectators));
  }
}

/** fun_move_broadcast (royale): apply a (self or other) score update. */
export function routeRoyaleMoveBroadcast(payload: {
  playerId: string;
  correct: number;
  wrong: number;
  nextIndex: number;
  combo?: number;
  points?: number;
  square?: string;
}): void {
  // Score broadcasts for the coach's race shouldn't touch the classroom
  // launcher tab's store (it's not the participant view). See onClassroomRoute.
  if (onClassroomRoute()) {
    return;
  }
  useFunChallengesRoyaleStore
    .getState()
    .applyMoveBroadcast(
      String(payload.playerId),
      payload.correct,
      payload.wrong,
      payload.nextIndex,
      nowMs(),
      payload.combo,
      payload.points,
      // `square` is present on the sender echo + every spectator broadcast;
      // it feeds the per-player trail used by the spectate board.
      typeof payload.square === 'string' ? payload.square : undefined,
    );
}

/** fun_spectate_move (royale): a player's per-ply board stream (spectate). */
export function routeRoyaleSpectateMove(payload: {
  playerId: string;
  fen: string;
  from: string;
  to: string;
  orientation?: 'white' | 'black';
  kind?: 'setup' | 'player' | 'reply' | 'wrong';
}): void {
  if (!payload || !payload.fen) {
    return;
  }
  useFunChallengesRoyaleStore
    .getState()
    .setLiveBoard(
      String(payload.playerId),
      payload.fen,
      payload.from || '',
      payload.to || '',
      payload.orientation === 'black' ? 'black' : 'white',
      payload.kind === 'setup' ||
        payload.kind === 'reply' ||
        payload.kind === 'wrong'
        ? payload.kind
        : 'player',
    );
}

/**
 * fun_spectate_start (royale): the server bootstrapped us as a SPECTATOR of a
 * live race. Same snapshot shape as fun_session_rejoin, but we hydrate via
 * hydrateSpectate (flags isSpectator + auto-follows the leader) so the screen
 * is read-only and we follow ongoing fun_move_broadcasts.
 */
export function routeRoyaleSpectateStart(
  payload: {
    sessionId: string;
    roomId: string;
    playerIds: string[];
    players?: RoomPlayerWire[];
    settings: RoyaleSettings;
    shareCode?: string;
    startsAt?: string;
    endsAt: string;
    scores: Record<string, any>;
    coords: string[];
    allResponses?: Record<string, string[]>;
  },
  myUserId: string,
): void {
  const specSettings: RoyaleSettings = {
    ...payload.settings,
    shareCode:
      (payload.settings as any)?.shareCode || payload.shareCode || '',
  };
  const session: RoyaleSession = {
    sessionId: payload.sessionId,
    roomId: payload.roomId,
    myUserId: String(myUserId),
    battleType: BattleType.PUZZLE_ROYALE,
    startsAt: parseInt(payload.startsAt || '', 10) || 0,
    endsAt: parseInt(payload.endsAt, 10) || 0,
    settings: specSettings,
    coords: payload.coords || [],
  };
  const players = toLobbyPlayers(payload.players, payload.settings?.hostId as string);
  const scores: Record<
    string,
    {points: number; solved: number; wrong: number; bestCombo: number}
  > = {};
  Object.entries(payload.scores || {}).forEach(([id, s]: [string, any]) => {
    scores[id] = {
      points: Number(s?.points) || 0,
      solved: Number(s?.correct) || 0,
      wrong: Number(s?.wrong) || 0,
      bestCombo: Number(s?.bestCombo) || 0,
    };
  });
  const store = useFunChallengesRoyaleStore.getState();
  store.hydrateSpectate(session, players, payload.allResponses || {}, scores);
  if ((payload as any).spectators) {
    store.setSpectators(toSpectators((payload as any).spectators));
  }
}

/** fun_player_eliminated (royale): mark a player out (stays ranked). */
export function routeRoyalePlayerEliminated(payload: {
  playerId: string;
}): void {
  useFunChallengesRoyaleStore.getState().markEliminated(String(payload.playerId));
}

/**
 * fun_rematch (royale): the host started a rematch. The server already created
 * the new room (host added) and pushed this to every ended-session player. We
 * seed a fresh optimistic lobby for everyone so they navigate to the waiting
 * room. Returns true if THIS client is the host — the caller leaves the host's
 * seeded lobby alone and makes every NON-host auto-join the new code (so the
 * server adds them + syncs the roster).
 */
export function routeRoyaleRematch(
  payload: {
    roomId: string;
    shareCode: string;
    hostId: string;
    settings?: RoyaleSettings;
  },
  myUserId: string,
): {isHost: boolean; shareCode: string} {
  const me = String(myUserId);
  const hostId = String(payload.hostId);
  const isHost = me === hostId;
  const shareCode = payload.shareCode || payload.roomId;
  useFunChallengesRoyaleStore.getState().setLobby(
    {
      roomId: payload.roomId,
      shareCode,
      hostId,
      hostIsPlayer: true,
      state: 'waiting',
      players: [
        {
          userId: me,
          name: '',
          avatarUrl: undefined,
          isHost,
        },
      ],
      spectators: [],
      settings: (payload.settings || {}) as RoyaleSettings,
      expiresAt: nowMs() + 15 * 60 * 1000,
    },
    me,
  );
  return {isHost, shareCode};
}

/** fun_session_end (royale): final standings. */
export function routeRoyaleSessionEnd(
  payload: {
    sessionId: string;
    gameType: string;
    winnerId: string;
    rankedPlayers: string[];
    scores: Record<string, any>;
    responses: Record<string, string[]>;
    reason: string;
  },
): void {
  // Normalise scores: server sends { correct, wrong, combo, bestCombo, points }.
  const scores: RoyaleEndPayload['scores'] = {};
  Object.entries(payload.scores || {}).forEach(([id, s]: [string, any]) => {
    scores[id] = {
      points: Number(s?.points) || 0,
      solved: Number(s?.correct) || 0,
      wrong: Number(s?.wrong) || 0,
      bestCombo: Number(s?.bestCombo) || 0,
    };
  });
  useFunChallengesRoyaleStore.getState().endRoyale({
    sessionId: payload.sessionId,
    battleType: BattleType.PUZZLE_ROYALE,
    reason: (payload.reason as RoyaleEndPayload['reason']) || 'time_up',
    rankedPlayers: payload.rankedPlayers || [],
    scores,
    responses: payload.responses || {},
  });
}

/** fun_session_rejoin (royale): bulk hydrate all players after a refresh. */
export function routeRoyaleSessionRejoin(
  payload: {
    sessionId: string;
    roomId: string;
    playerIds: string[];
    players?: RoomPlayerWire[];
    settings: RoyaleSettings;
    startsAt?: string;
    endsAt: string;
    scores: Record<string, any>;
    coords: string[];
    allResponses?: Record<string, string[]>;
    watcherId?: string;
  },
  myUserId: string,
): void {
  // The classroom launcher tab fires an auto-rejoin probe; a positive reply
  // (the coach's active session, since they're a player) would hydrate THIS tab
  // into 'in_battle' and dirty it for the next create. The coach's actual race
  // lives in the battle tab — ignore rejoin on the classroom route.
  if (onClassroomRoute()) {
    return;
  }
  // Recover the watcher id on rejoin: prefer the explicit payload field, else
  // derive it from settings (hostWatching + hostId ride the settings blob).
  const rejoinWatcherId =
    String(payload.watcherId || '') ||
    ((payload.settings as any)?.hostWatching === true &&
    (payload.settings as any)?.hostId
      ? String((payload.settings as any).hostId)
      : '');
  const session: RoyaleSession = {
    sessionId: payload.sessionId,
    roomId: payload.roomId,
    myUserId: String(myUserId),
    battleType: BattleType.PUZZLE_ROYALE,
    startsAt: parseInt(payload.startsAt || '', 10) || 0,
    endsAt: parseInt(payload.endsAt, 10) || 0,
    settings: payload.settings,
    coords: payload.coords || [],
    watcherId: rejoinWatcherId,
  };
  const players = toLobbyPlayers(payload.players, payload.settings?.hostId as string);
  const scores: Record<
    string,
    {points: number; solved: number; wrong: number; bestCombo: number}
  > = {};
  Object.entries(payload.scores || {}).forEach(([id, s]: [string, any]) => {
    scores[id] = {
      points: Number(s?.points) || 0,
      solved: Number(s?.correct) || 0,
      wrong: Number(s?.wrong) || 0,
      bestCombo: Number(s?.bestCombo) || 0,
    };
  });
  useFunChallengesRoyaleStore
    .getState()
    .hydrateRoyale(session, players, payload.allResponses || {}, scores);
}
